import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Search } from 'lucide-react';
import { getApiBase } from '../apiBase';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 60000,
});

const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
];

function formatApiError(res, fallback = 'Request failed') {
  const msg = res.data?.error || fallback;
  if (res.status === 404) {
    return (
      'API route not found (HTTP 404). Restart the API from latest main (scripts/restart-dev.ps1) — ' +
      'required routes: GET /api/copilot/bpm/attributes, GET /api/hcm/pod-catalog/columns, ' +
      'GET /api/hcm/attribute-values, POST /api/copilot/bpm/generate-rule-from-nl. ' +
      'Check GET /api/health features.podCatalogColumns and bpmGenerateRuleFromNl.'
    );
  }
  if (res.status === 401) {
    return 'Not signed in. Log in again, then retry.';
  }
  if (res.status === 503) {
    return msg || 'HCM connection not configured.';
  }
  return msg;
}

function useDebouncedFetch(fetchFn, deps, delayMs = 300) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const next = await fetchFn();
        setItems(next);
      } catch (err) {
        setItems([]);
        setError(err.message || 'Request failed');
      } finally {
        setLoading(false);
      }
    }, delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { loading, items, error };
}

function SuggestionList({ items, onPick, emptyText, highlightKey }) {
  if (!items.length) {
    return emptyText ? (
      <p className="px-2 py-1.5 text-[10px] text-slate-500">{emptyText}</p>
    ) : null;
  }
  return (
    <ul className="max-h-44 overflow-y-auto py-1" role="listbox">
      {items.map((item) => (
        <li key={item[highlightKey] || item.value || item.tableColumn}>
          <button
            type="button"
            role="option"
            className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-cyan-50 text-slate-800"
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(item);
            }}
          >
            {item.label || item.value}
            {item.tableColumn && (
              <span className="block text-[10px] text-slate-400 truncate">{item.tableColumn}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Attribute + value typeaheads backed by pod catalog and /api/hcm/attribute-values.
 */
export default function HcmConditionAutocomplete({
  value,
  onChange,
  disabled = false,
  bpmCategory = '',
}) {
  const table = value?.table || '';
  const column = value?.column || '';
  const operator = value?.operator || 'equals';
  const attrValue = value?.value ?? '';

  const [attrQuery, setAttrQuery] = useState(
    table && column ? `${table}.${column}` : ''
  );
  const [valueQuery, setValueQuery] = useState(attrValue);
  const [attrOpen, setAttrOpen] = useState(false);
  const [valueOpen, setValueOpen] = useState(false);

  useEffect(() => {
    if (table && column) {
      setAttrQuery(`${table}.${column}`);
    }
  }, [table, column]);

  useEffect(() => {
    setValueQuery(attrValue);
  }, [attrValue]);

  const attrSearch = useDebouncedFetch(
    async () => {
      if (!attrOpen || disabled) return [];
      const q = attrQuery.includes('.') ? attrQuery.split('.').pop() : attrQuery;
      const items = [];
      if (bpmCategory) {
        const bpmRes = await API.get('/api/copilot/bpm/attributes', {
          params: { category: bpmCategory, q, limit: 20 },
          validateStatus: () => true,
        });
        if (bpmRes.status === 200) {
          for (const attr of bpmRes.data?.items || bpmRes.data?.attributes || []) {
            items.push({
              table: attr.table,
              column: attr.column,
              label: attr.label,
              tableColumn: attr.tableColumn,
              factType: attr.factType,
              source: 'bpm-registry',
            });
          }
        }
      }
      const res = await API.get('/api/hcm/pod-catalog/columns', {
        params: { q, limit: 20 },
        validateStatus: () => true,
      });
      if (res.status === 200) {
        const seen = new Set(items.map((i) => i.tableColumn));
        for (const item of res.data?.items || []) {
          if (!seen.has(item.tableColumn)) {
            items.push(item);
            seen.add(item.tableColumn);
          }
        }
      } else if (!items.length) {
        throw new Error(formatApiError(res, `HTTP ${res.status}`));
      }
      return items;
    },
    [attrQuery, attrOpen, disabled, bpmCategory],
    280
  );

  const valueSearch = useDebouncedFetch(
    async () => {
      if (!valueOpen || disabled || !table || !column) return [];
      const res = await API.get('/api/hcm/attribute-values', {
        params: { table, column, q: valueQuery, limit: 25 },
        validateStatus: () => true,
      });
      if (res.status !== 200) throw new Error(formatApiError(res, `HTTP ${res.status}`));
      return (res.data?.values || []).map((v) => ({
        value: v.value,
        label: v.label || v.value,
      }));
    },
    [table, column, valueQuery, valueOpen, disabled],
    280
  );

  function pickAttribute(item) {
    setAttrQuery(item.tableColumn);
    setAttrOpen(false);
    onChange({
      ...value,
      table: item.table,
      column: item.column,
      ...(item.factType ? { attribute: item.factType, factType: item.factType } : {}),
      operator,
      value: '',
    });
    setValueQuery('');
  }

  function pickValue(item) {
    setValueQuery(item.value);
    setValueOpen(false);
    onChange({
      ...value,
      table,
      column,
      operator,
      value: item.value,
    });
  }

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
        <div className="relative">
          <label className="block text-[10px] font-medium uppercase tracking-wide text-cyan-900 mb-1">
            Attribute
          </label>
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" aria-hidden />
            <input
              type="text"
              className="w-full rounded-md border border-cyan-200 bg-white py-1.5 pl-7 pr-8 text-xs text-slate-800 disabled:opacity-50"
              placeholder={
                bpmCategory ? 'e.g. absence type, duration' : 'e.g. assignment status'
              }
              value={attrQuery}
              disabled={disabled}
              onChange={(e) => {
                setAttrQuery(e.target.value);
                setAttrOpen(true);
              }}
              onFocus={() => setAttrOpen(true)}
              onBlur={() => setTimeout(() => setAttrOpen(false), 150)}
            />
            {attrSearch.loading && (
              <Loader2 className="absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-cyan-600" />
            )}
          </div>
          {attrOpen && !disabled && (
            <div className="absolute z-20 mt-0.5 w-full rounded-md border border-cyan-200 bg-white shadow-md">
              <SuggestionList
                items={attrSearch.items}
                highlightKey="tableColumn"
                onPick={pickAttribute}
                emptyText={
                  attrSearch.error
                    ? attrSearch.error
                    : attrSearch.loading
                      ? 'Searching…'
                      : bpmCategory
                      ? 'Type to search registered attributes'
                      : 'Type to search pod columns'
                }
              />
            </div>
          )}
        </div>

        <div className="sm:pt-5">
          <label className="block text-[10px] font-medium uppercase tracking-wide text-cyan-900 mb-1 sm:sr-only">
            Operator
          </label>
          <select
            className="w-full rounded-md border border-cyan-200 bg-white py-1.5 px-2 text-xs text-slate-800 disabled:opacity-50"
            value={operator}
            disabled={disabled || !column}
            onChange={(e) =>
              onChange({ ...value, table, column, operator: e.target.value, value: attrValue })
            }
          >
            {OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>

        <div className="relative">
          <label className="block text-[10px] font-medium uppercase tracking-wide text-cyan-900 mb-1">
            Value
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full rounded-md border border-cyan-200 bg-white py-1.5 px-2 pr-8 text-xs text-slate-800 disabled:opacity-50"
              placeholder={column ? 'e.g. Active' : 'Select attribute first'}
              value={valueQuery}
              disabled={disabled || !column}
              onChange={(e) => {
                setValueQuery(e.target.value);
                setValueOpen(true);
                onChange({ ...value, table, column, operator, value: e.target.value });
              }}
              onFocus={() => column && setValueOpen(true)}
              onBlur={() => setTimeout(() => setValueOpen(false), 150)}
            />
            {valueSearch.loading && (
              <Loader2 className="absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-cyan-600" />
            )}
          </div>
          {valueOpen && column && !disabled && (
            <div className="absolute z-20 mt-0.5 w-full rounded-md border border-cyan-200 bg-white shadow-md">
              <SuggestionList
                items={valueSearch.items}
                highlightKey="value"
                onPick={pickValue}
                emptyText={
                  valueSearch.error ||
                  (valueSearch.loading ? 'Loading values…' : 'No suggestions — type a value')
                }
              />
            </div>
          )}
        </div>
      </div>
      {table && column && (
        <p className="text-[10px] text-slate-500">
          Catalog: <span className="font-mono text-slate-600">{table}.{column}</span>
        </p>
      )}
    </div>
  );
}
